import { FC, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { useForm } from '@tanstack/react-form';

import {
  InputPassword,
  InputText,
  Button,
  ButtonText,
  TextBlock,
  useModal,
  FormField,
  SubmitButtonWrapper,
  Title,
} from '../../components';
import { encode64, EMAIL_SYMBOLS_REGEX } from '@skillarea/lib-shared';
import { createValidator } from '@skillarea/lib-shared';

import { TOAuthButtonsTexts, TRegisterTexts } from '../../types';
import { OnboardRegisterDto } from '@skillarea/lib-shared';

import { OAuthButtons } from './OAuthButtons';
import styles from './Widgets.module.scss';
import { EOnboardRoutes } from '../index';
import { InputCheckbox } from '../../components/inputs/InputCheckbox';

const registerValidator = createValidator(OnboardRegisterDto);

function getRegistrationErrorMessage(
  result: unknown,
  fallback: string,
): string {
  if (!result || typeof result !== 'object') {
    return fallback;
  }

  if ('dtoMessages' in result && Array.isArray(result.dtoMessages)) {
    const emailMessage = result.dtoMessages.find((dtoMessage) => {
      return (
        dtoMessage &&
        typeof dtoMessage === 'object' &&
        'field' in dtoMessage &&
        'message' in dtoMessage &&
        dtoMessage.field === 'email' &&
        typeof dtoMessage.message === 'string'
      );
    });

    if (
      emailMessage &&
      typeof emailMessage === 'object' &&
      'message' in emailMessage &&
      typeof emailMessage.message === 'string'
    ) {
      return emailMessage.message;
    }

    const firstDtoMessage = result.dtoMessages.find((dtoMessage) => {
      return (
        dtoMessage &&
        typeof dtoMessage === 'object' &&
        'message' in dtoMessage &&
        typeof dtoMessage.message === 'string'
      );
    });

    if (
      firstDtoMessage &&
      typeof firstDtoMessage === 'object' &&
      'message' in firstDtoMessage &&
      typeof firstDtoMessage.message === 'string'
    ) {
      return firstDtoMessage.message;
    }
  }

  if ('message' in result && typeof result.message === 'string') {
    return result.message;
  }

  if ('error' in result && typeof result.error === 'string') {
    return result.error;
  }

  return fallback;
}

export const Register: FC<{
  oauthTexts: TOAuthButtonsTexts;
  texts: TRegisterTexts;
  onboardStore: any;
  onRegisterSuccess?: () => void;
}> = ({ oauthTexts, texts, onboardStore, onRegisterSuccess }) => {
  const { closeModal } = useModal();
  const location = useLocation();
  const navigate = useNavigate();
  const [registrationErrorMessage, setRegistrationErrorMessage] = useState('');

  const form = useForm({
    defaultValues: {
      email: '',
      name: '',
      password: '',
      repeatPassword: '',
      agreements: false,
    },
    onSubmit: async ({ value }) => {
      setRegistrationErrorMessage('');

      const result = await onboardStore.getState().register(
        { body: value },
        {
          errorInterceptor: () => true,
        },
      );
      if (result.success) {
        onRegisterSuccess?.();
        void navigate({
          to: location.pathname,
          hash: EOnboardRoutes.activate.replace(':link', encode64(value.email)),
        });
        closeModal();
      } else {
        setRegistrationErrorMessage(
          getRegistrationErrorMessage(result, texts.emailAlreadyUsed),
        );
      }
    },
    validators: {
      onChange: registerValidator,
      onSubmit: registerValidator,
    },
  });

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <Title className={styles.header} color="accent" size="xs">
        {texts.title}
      </Title>

      <OAuthButtons isRegister texts={oauthTexts} />

      <div className={styles.fields}>
        <FormField name="email" form={form}>
          <InputText
            size="xl"
            allowedPattern={EMAIL_SYMBOLS_REGEX}
            className={styles.topInput}
            placeholder={texts.emailPlaceholder}
          />
        </FormField>

        <FormField name="name" form={form}>
          <InputText size="xl" placeholder={texts.namePlaceholder} />
        </FormField>

        <FormField name="password" form={form}>
          <InputPassword size="xl" placeholder={texts.passwordPlaceholder} />
        </FormField>

        <FormField name="repeatPassword" form={form}>
          <InputPassword
            size="xl"
            placeholder={texts.passwordRepeatPlaceholder}
          />
        </FormField>
      </div>

      <FormField name="agreements" form={form}>
        <InputCheckbox
          className={styles.checkbox}
          color="white"
          label={
            <TextBlock
              className={styles.policyContainer}
              color="noteContrast"
              line="150"
              size="s"
            >
              {texts.bySign}{' '}
              <a
                href="/terms"
                rel="noopener noreferrer"
                target="_blank"
                className={styles.policy}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {texts.policy}
              </a>
            </TextBlock>
          }
        />
      </FormField>

      {registrationErrorMessage ? (
        <TextBlock color="error" size="s" data-testid="register-error-message">
          {registrationErrorMessage}
        </TextBlock>
      ) : null}

      <SubmitButtonWrapper form={form}>
        <Button
          className={styles.noTextSubmit}
          type="submit"
          variant="filled"
          color="primary"
        >
          {texts.create}
        </Button>
      </SubmitButtonWrapper>

      <div className={styles.footer}>
        <div className={styles.bottomSingleLink}>
          <TextBlock size="s" color="noteContrast">
            {texts.hasAccount}
          </TextBlock>
          <ButtonText
            color="accent"
            size="xs"
            onClick={() => {
              void navigate({
                to: location.pathname,
                hash: EOnboardRoutes.login,
              });
            }}
          >
            {texts.login}
          </ButtonText>
        </div>
      </div>
    </form>
  );
};
